export const ACTION_VERBS =
  'choose|chose|set|update|change|use|select|pick|apply|assign|make|map|default|ensure|switch|mark|record|book|categorise|categorize|classify|put';

export const ACTION_VERB_IN_SENTENCE = new RegExp(
  `(?:then\\s+)?(?:please\\s+)?(?:${ACTION_VERBS})\\s+`,
  'gi'
);

const VERB_CONJUGATIONS: [RegExp, string][] = [
  [/\bsets\b/gi, 'set'],
  [/\bchooses\b/gi, 'choose'],
  [/\bchose\b/gi, 'choose'],
  [/\bupdates\b/gi, 'update'],
  [/\bchanges\b/gi, 'change'],
  [/\buses\b/gi, 'use'],
  [/\bselects\b/gi, 'select'],
  [/\bpicks\b/gi, 'pick'],
  [/\bapplies\b/gi, 'apply'],
  [/\bassigns\b/gi, 'assign'],
  [/\bmakes\b/gi, 'make'],
  [/\bmaps\b/gi, 'map'],
  [/\bswitches\b/gi, 'switch'],
  [/\bmarks\b/gi, 'mark'],
  [/\brecords\b/gi, 'record'],
  [/\bbooks\b/gi, 'book'],
  [/\bcategorises\b/gi, 'categorise'],
  [/\bcategorizes\b/gi, 'categorize'],
  [/\bclassifies\b/gi, 'classify'],
  [/\bputs\b/gi, 'put'],
];

const RULE_META_PREFIXES = [
  /^(?:(?:please|kindly)\s+)?(?:(?:can|could)\s+you\s+)?(?:help\s+me\s+(?:to\s+)?)?(?:(?:i\s+)?(?:want|need)\s+(?:to\s+)?)?(?:(?:create|add|build|make|set\s+up|write|define|generate|establish|draft)\s+)?(?:a|an|the|my|new)?\s*rules?\s*(?::|—|–|-)?\s*(?:that|which|where|to|so\s+that|so)\s+/i,
  /^(?:(?:please|kindly)\s+)?(?:(?:can|could)\s+you\s+)?(?:help\s+me\s+(?:to\s+)?)?(?:(?:i\s+)?(?:want|need)\s+(?:to\s+)?)?(?:(?:create|add|build|make|set\s+up|write|define|generate|establish|draft)\s+)?(?:a|an|the|my|new)?\s*rules?\s*(?::|—|–|-)\s+/i,
  /^(?:(?:please|kindly)\s+)?(?:i(?:'d|\s+would)\s+like\s+(?:(?:you\s+)?to\s+)?)?(?:(?:create|add|build|make|set\s+up|write|define|generate|establish|draft)\s+)?(?:a|an|the|my|new)?\s*rules?\s*(?::|—|–|-)?\s*(?:that|which|where|to|so\s+that|so)\s+/i,
  /^(?:(?:please|kindly)\s+)?(?:give\s+me\s+)?(?:a|an|the|my|new)?\s*rules?\s+(?:that|which|where|to|so\s+that|so)\s+/i,
  /^rules?\s*(?::|—|–|-)\s*/i,
  /^new\s+rules?\s*(?::|—|–|-)?\s*/i,
];

/** Normalise user text before pattern matching. */
export function normalizeRuleText(text: string): string {
  let result = text
    .trim()
    .replace(/\s+/g, ' ')
    .replace(/[''`]/g, "'")
    .replace(/[;:]+/g, ',')
    .replace(/\s+,/g, ',')
    .replace(/,\s*/g, ', ')
    .replace(/\bisn't\b/gi, 'is not')
    .replace(/\baren't\b/gi, 'are not')
    .replace(/\bdoesn't\b/gi, 'does not')
    .replace(/\bdon't\b/gi, 'do not')
    .replace(/\bcan't\b/gi, 'cannot')
    .replace(/\bwon't\b/gi, 'will not')
    .replace(/\bhasn't\b/gi, 'has not')
    .replace(/\.+$/g, '')
    .replace(/\bwhenever\b/gi, 'when')
    .replace(/\beach time\b/gi, 'when')
    .replace(/\bevery time\b/gi, 'when')
    .replace(/\bany time\b/gi, 'when')
    .replace(/\bin cases where\b/gi, 'when')
    .replace(/\bin case\b/gi, 'when')
    .replace(/\bon condition that\b/gi, 'when')
    .replace(/\bprovided that\b/gi, 'when')
    .replace(/\bas long as\b/gi, 'when')
    .replace(/\bfor (?:documents|transactions|lines) where\b/gi, 'when')
    .replace(/\bon (?:documents|transactions|lines) where\b/gi, 'when');

  for (const [pattern, base] of VERB_CONJUGATIONS) {
    result = result.replace(pattern, base);
  }

  return result;
}

export function stripLeadingModifiers(text: string): string {
  return text
    .replace(/^(?:automatically|by\s+default|please)\s+/i, '')
    .trim();
}

/** Strip conversational wrappers like "create a rule that …". */
export function stripRuleMetaPhrases(text: string): string {
  let result = text.trim();
  let changed = true;

  while (changed) {
    changed = false;
    for (const pattern of RULE_META_PREFIXES) {
      const next = result.replace(pattern, '').trim();
      if (next !== result) {
        result = next;
        changed = true;
        break;
      }
    }
  }

  return result;
}

/** First action verb onward, e.g. "create a rule that choose customer X" → "choose customer X". */
export function extractLeadingActionPhrase(text: string): string {
  const verbRegex = new RegExp(ACTION_VERB_IN_SENTENCE.source, 'gi');
  const match = verbRegex.exec(text);
  if (match && match.index >= 0) {
    return text.slice(match.index).trim();
  }
  return text;
}

/**
 * Split "… action … when/if condition" using the last when/if so nested wording is ignored.
 */
export function splitActionConditionSuffix(
  input: string
): { actionPart: string; conditionPart: string } | null {
  if (/^(?:when|if)\s+/i.test(input)) return null;

  const suffixRegex = /\s+(?:when|if)\s+/gi;
  let lastIndex = -1;
  let match: RegExpExecArray | null;
  while ((match = suffixRegex.exec(input)) !== null) {
    lastIndex = match.index;
  }
  if (lastIndex < 0) return null;

  const actionPart = input.slice(0, lastIndex).trim();
  const conditionPart = input.slice(lastIndex).replace(/^\s+(?:when|if)\s+/i, '').trim();
  if (!actionPart || !conditionPart) return null;

  return { actionPart, conditionPart };
}

export function preprocessRuleText(rawInput: string): string {
  return stripLeadingModifiers(
    stripRuleMetaPhrases(normalizeRuleText(rawInput))
  );
}
